import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { isFromYjs } from './auto-joiner-extension';

const NOT_AFTER = 'paragraph';

/**
 * The gapcursor cannot land below a final list or heading (they end in
 * inline content), so a click there appends the paragraph the user is
 * reaching for.
 */
function clickBelowLastBlock(view: EditorView, event: MouseEvent): boolean {
  if (!view.editable) return false;
  const { doc } = view.state;
  const last = doc.lastChild;
  if (!last || last.type.name === NOT_AFTER) return false;
  const dom = view.nodeDOM(doc.content.size - last.nodeSize);
  if (!(dom instanceof HTMLElement) || event.clientY <= dom.getBoundingClientRect().bottom) return false;
  const tr = view.state.tr.insert(doc.content.size, view.state.schema.nodes[NOT_AFTER].create());
  tr.setSelection(TextSelection.create(tr.doc, tr.doc.content.size - 1));
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Keeps an empty paragraph after a final list, heading, code block or quote
 * so the user can type below it — but only after the user's own edit in an
 * editable view. StarterKit's TrailingNode appends it to whatever the doc
 * holds, including content only received from Yjs, which Collaboration then
 * sends back as this client's edit.
 */
export const TrailingParagraph = Extension.create({
  name: 'trailingParagraph',

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: new PluginKey('trailingParagraph'),
        props: {
          handleClick: (view, _pos, event) => clickBelowLastBlock(view, event),
        },
        appendTransaction: (transactions, _oldState, newState) => {
          if (!editor.isEditable) return null;
          if (!transactions.some((tr) => tr.docChanged && !isFromYjs(tr))) return null;
          const last = newState.doc.lastChild;
          if (!last || last.type.name === NOT_AFTER) return null;
          const paragraph = newState.schema.nodes[NOT_AFTER];
          return newState.tr.insert(newState.doc.content.size, paragraph.create());
        },
      }),
    ];
  },
});
